/**
 * Admin-scoped per-user audit-events drilldown hook (#990).
 *
 * Calls ``GET /v1/admin/users/{userId}/audit-events`` (the backend
 * endpoint shipped in subtask 02-990, PR #1007). That endpoint mints
 * a server-side UDB session for the target tenant under the calling
 * admin's cookie session and forwards the wire params verbatim to
 * UDB's ``GET /audit/events``. The defaults
 * ``actor_type='admin_impersonation'`` + ``subject_user_id={user_id}`` are
 * locked at the backend layer (HOF-685: the impersonated person is the row's
 * subject and the admin its ``actor_id``; the manifest's "actor_id filter trap" —
 * tenants shared between a farmer and their employees would otherwise
 * leak the farmer's row into an employee's drilldown).
 *
 * The hook returns the raw ``next_cursor`` so the page component can
 * drive forward+back pagination from a client-side cursor stack
 * (UDB's keyset is opaque and forward-only — backward navigation is
 * handled by remembering visited cursors, not by a server-side
 * ``prev_cursor``).
 *
 * Why a separate hook from ``useAuditEvents``? The existing hook's
 * shape is tightly coupled to the lens-tree (``category``/
 * ``subcategory`` → ``event_type IN (...)``) which has no meaning for
 * the impersonation drilldown, and cursor pagination would inflate
 * its API. Mapping helpers are still shared via
 * ``auditEventMappers``, so display formatting stays consistent.
 */
import { useQuery } from '@tanstack/react-query';
import type { AuditLogEntry } from '@hofsync/design-system';
import api from '../services/api';
import {
  mapServerEventToEntry,
  type ServerAuditEventsResponse,
} from './auditEventMappers';

export interface UseAdminAuditEventsParams {
  userId: string;
  /** Opaque UDB keyset cursor; omit for the first page. */
  cursor?: string;
  /** ISO-8601 window lower bound (inclusive). */
  since?: string;
  /** ISO-8601 window upper bound (inclusive). */
  until?: string;
  /** Restrict to mutations performed by a specific admin. */
  impersonatorUserId?: string;
  /**
   * Page size (1–200, mirrors UDB's hard cap). Default 50 matches the
   * backend endpoint's default.
   */
  limit?: number;
}

export interface UseAdminAuditEventsResult {
  entries: AuditLogEntry[];
  /** Cursor for the next page; ``null`` on the last page or before first load. */
  nextCursor: string | null;
  /** Effective page size echoed by the server. */
  limit: number;
  isLoading: boolean;
  isError: boolean;
  error?: Error;
  refetch: () => void;
}

const DEFAULT_LIMIT = 50;

function buildParams(
  params: UseAdminAuditEventsParams,
): Record<string, string | number> {
  const out: Record<string, string | number> = {
    limit: params.limit ?? DEFAULT_LIMIT,
  };
  if (params.cursor) out.cursor = params.cursor;
  if (params.since) out.since = params.since;
  if (params.until) out.until = params.until;
  if (params.impersonatorUserId) {
    out.impersonator_user_id = params.impersonatorUserId;
  }
  return out;
}

export function useAdminAuditEvents(
  params: UseAdminAuditEventsParams,
): UseAdminAuditEventsResult {
  const url = `/v1/admin/users/${params.userId}/audit-events`;
  const queryParams = buildParams(params);

  const query = useQuery<ServerAuditEventsResponse>({
    queryKey: [
      'admin-audit-events',
      params.userId,
      params.cursor ?? null,
      params.since ?? null,
      params.until ?? null,
      params.impersonatorUserId ?? null,
      params.limit ?? DEFAULT_LIMIT,
    ],
    queryFn: async () => {
      const res = await api.get<ServerAuditEventsResponse>(url, {
        params: queryParams,
      });
      return res.data;
    },
    enabled: Boolean(params.userId),
  });

  const rawEvents = query.data?.events ?? [];
  const entries = rawEvents.map(mapServerEventToEntry);

  return {
    entries,
    nextCursor: query.data?.page.next_cursor ?? null,
    limit: query.data?.page.limit ?? params.limit ?? DEFAULT_LIMIT,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : undefined,
    refetch: () => {
      void query.refetch();
    },
  };
}
